import React from "react";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { FeedbackI, GeneralFeedbackTypeEnum } from "../types";
import {
  formatFeedbackDate,
  getUsefulnessLabel,
  getFeedbackTypeLabel,
} from "../utils/feedbackUtils";

interface FeedbackCardProps {
  feedback: FeedbackI;
  context?: string; // Optional - for showing module/lesson context in analytics
  /** When set with `context`, context is rendered as a button (e.g. open lesson in new tab). */
  onContextClick?: () => void;
  showEmptyState?: boolean; // Whether to show "No content provided" for empty feedback
  canResolve?: boolean; // Whether the current user can mark feedback as resolved
  onResolveToggle?: (feedbackId: string, isResolved: boolean) => void;
  isResolving?: boolean; // Loading state while resolving
}

const FeedbackCard: React.FC<FeedbackCardProps> = ({
  feedback,
  context,
  onContextClick,
  showEmptyState = false,
  canResolve = false,
  onResolveToggle,
  isResolving = false,
}) => {
  const usefulnessLabel = getUsefulnessLabel(feedback.feedback);
  const hasSubFeedback =
    feedback.subFeedback && feedback.subFeedback.length > 0;
  const hasOtherFeedback =
    feedback.otherFeedback && feedback.otherFeedback.trim() !== "";
  const hasOverallRating =
    feedback.feedback !== undefined && feedback.feedback !== null;
  const isLessonFeedback =
    feedback.feedbackType === GeneralFeedbackTypeEnum.LESSON_FEEDBACK ||
    (feedback.feedbackType as string) === "lesson_feedback";
  const isResolved = feedback.isResolved === true;

  return (
    <div
      className={`border-2 rounded-lg p-4 transition-colors ${
        isResolved
          ? "border-green-200 bg-green-50 opacity-75"
          : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          {feedback.pageNumber !== undefined && (
            <span className="px-2 py-1 bg-electrum-100 text-electrum-700 text-xs font-semibold rounded">
              Page {feedback.pageNumber}
            </span>
          )}
          {feedback.feedbackType && (
            <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded">
              {getFeedbackTypeLabel(feedback.feedbackType)}
            </span>
          )}
          {isResolved && (
            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded flex items-center gap-1">
              <CheckCircleIcon className="w-3 h-3" />
              Resolved
            </span>
          )}
          {context &&
            (onContextClick ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onContextClick();
                }}
                className="text-xs text-electrum-600 hover:text-electrum-800 hover:underline cursor-pointer font-medium transition-colors text-left"
              >
                {context}
              </button>
            ) : (
              <span className="text-xs text-gray-500">{context}</span>
            ))}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {feedback?.userProfile?.segments?.length > 0 ? (
            <div className="flex items-center gap-1">
              {feedback.userProfile.segments.map((segment) => (
                <span
                  key={segment}
                  className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded"
                >
                  {segment}
                </span>
              ))}
            </div>
          ) : (
            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded">
              No Segment
            </span>
          )}
          <span className="text-xs text-gray-500">
            {formatFeedbackDate(feedback.createdAt)}
          </span>
          {canResolve && feedback._id && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isResolved}
                onChange={() => onResolveToggle?.(feedback._id!, !isResolved)}
                disabled={isResolving}
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 disabled:opacity-50"
              />
              <span className="text-sm text-gray-700">Resolved</span>
            </label>
          )}
        </div>
      </div>

      {/* Overall Rating - Only show for lesson feedback */}
      {isLessonFeedback && hasOverallRating && (
        <div className="mb-2">
          <span className="text-xs font-semibold text-gray-600 mr-2">
            Overall Rating:
          </span>
          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
            {usefulnessLabel}
          </span>
        </div>
      )}

      {/* Comment/Other Feedback */}
      {hasOtherFeedback && (
        <div className="text-sm text-gray-700 whitespace-pre-wrap mb-2">
          {feedback.otherFeedback}
        </div>
      )}

      {/* Sub Feedback */}
      {isLessonFeedback && hasSubFeedback && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="text-xs font-semibold text-gray-600 mb-2">
            Follow-up Responses:
          </div>
          <ul className="list-disc list-inside space-y-1">
            {feedback.subFeedback?.map((item, index) => (
              <li key={index} className="text-sm text-gray-700">
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Empty state */}
      {showEmptyState && !hasOtherFeedback && !hasSubFeedback && (
        <div className="text-sm text-gray-500 italic">No content provided</div>
      )}

      {feedback.canContact && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
              Can Contact
            </span>
            {(feedback?.userProfile?.username ||
              feedback?.userProfile?.email) && (
              <span className="text-sm text-gray-600">
                {feedback.userProfile.username || feedback.userProfile.email}
                {feedback.userProfile.username &&
                  feedback.userProfile.email && (
                    <span className="text-gray-500">
                      {" "}
                      ({feedback.userProfile.email})
                    </span>
                  )}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackCard;
